import { FC, ReactNode } from 'react';
import type { FrescorReplica, PlanoForaDoFiltro, VisaoGeralMes, VisaoGeralPlano } from '@/types/dashboards';
import type { DivergenciaCodigo } from '@/utils/equiplexVisaoGeral';
import { fmtData, fmtDataHora, fmtUn } from '@/utils/painelFormat';
import Aviso from '../Aviso';
import { STATUS } from '../chartTheme';

interface Props {
  frescor: FrescorReplica;
  plano: VisaoGeralPlano;
  mes: VisaoGeralMes;
  mesTemPlano: boolean;
  corte: string;
  /** Último dia fechado (corte − 2 dias): até ele as OPs já foram reportadas. */
  fechadoAte: string;
  divergencias: DivergenciaCodigo[];
  /** Plano do mesmo formato em código fora do filtro de produto (do período inteiro). */
  foraDoFiltro: PlanoForaDoFiltro[];
}

const codigo = (l: { codigoDatasul: string | null; codigo: string }) => l.codigoDatasul ?? l.codigo;

function avisoPlano(plano: VisaoGeralPlano, mes: VisaoGeralMes, mesTemPlano: boolean): ReactNode {
  if (plano.status === 'indisponivel') {
    return (
      <Aviso key="plano" testId="aviso-plano-indisponivel" icone="sync_problem" cor={STATUS.critico} titulo="Plano S&OP não carregou">
        {plano.mensagem ?? 'Não foi possível ler o Balanceamento de Linha.'} O realizado e os blocos por máquina seguem valendo.
      </Aviso>
    );
  }
  if (!mesTemPlano) {
    return (
      <Aviso key="plano" testId="aviso-sem-plano" icone="event_busy" cor={STATUS.atencao} titulo={`${mes.label} sem plano S&OP`}>
        O Balanceamento de Linha não tem demanda para este mês ({mes.range}). O realizado aparece sem meta até o mês ser
        cadastrado lá.
      </Aviso>
    );
  }
  return null;
}

function avisoForaDoFiltro(fora: PlanoForaDoFiltro[]): ReactNode {
  if (fora.length === 0) return null;
  return (
    <Aviso key="fora-do-filtro" testId="aviso-plano-fora-do-filtro" icone="filter_alt" cor={STATUS.atencao} titulo="Plano do formato em outro código">
      O S&OP do período para este formato está em{' '}
      {fora.map((p, i) => (
        <span key={p.codigo}>
          {i > 0 && ', '}
          <strong>{p.codigo}</strong> {p.descricao} ({fmtUn(p.quantidade)} un)
        </span>
      ))}
      , fora do filtro de produto. Inclua no filtro para comparar o plano com a produção.
    </Aviso>
  );
}

/** O que muda a leitura dos números: réplica parada, plano ausente, mês aberto, código trocado. */
const AvisosVisaoGeral: FC<Props> = ({ frescor, plano, mes, mesTemPlano, corte, fechadoAte, divergencias, foraDoFiltro }) => {
  const avisos: ReactNode[] = [];
  if (frescor.replica_atrasada) {
    avisos.push(
      <Aviso key="frescor" testId="aviso-replica" icone="cloud_off" cor={STATUS.critico} titulo="Réplica do Datasul parada">
        A última carga foi em {fmtDataHora(frescor.ultima_carga_replica)}
        {frescor.dias_sem_carga !== null && <> ({frescor.dias_sem_carga} dias)</>}. O realizado vai só até{' '}
        {fmtData(frescor.producao_ate)}.
      </Aviso>,
    );
  }
  const plan = avisoPlano(plano, mes, mesTemPlano);
  if (plan) avisos.push(plan);
  const fora = avisoForaDoFiltro(foraDoFiltro);
  if (fora) avisos.push(fora);
  if (mes.em_andamento) {
    avisos.push(
      <Aviso key="parcial" testId="aviso-parcial" icone="hourglass_top" cor={STATUS.atencao} titulo={`${mes.label} ainda está aberto`}>
        Produção reportada até {fmtData(corte)}: {mes.dias_decorridos} de {mes.dias} dias. A OP é reportada de uma vez no fim do processo,
        então a produção das OPs que começaram depois de {fmtData(fechadoAte)} ainda está chegando: o ritmo, a média e o
        desvio por máquina só contam os dias fechados, até {fmtData(fechadoAte)}. O produzido do mês cresce até as OPs fecharem.
      </Aviso>,
    );
  }
  if (divergencias.length > 0) {
    avisos.push(
      <Aviso key="codigo" testId="aviso-codigo" icone="swap_horiz" cor={STATUS.atencao} titulo="Plano e produção em códigos diferentes">
        {divergencias.map((d, i) => (
          <span key={d.categoria}>
            {i > 0 && '; '}
            <strong>{d.categoria}</strong>: o plano cita {d.semProducao.map(codigo).join(', ')}, sem produção, e a fábrica
            produziu {d.foraDoPlano.map(codigo).join(', ')}, fora do plano
          </span>
        ))}
        . Parece troca de código no cadastro do Balanceamento. A comparação por formato não é afetada; a por SKU, sim.
      </Aviso>,
    );
  }
  if (avisos.length === 0) return null;
  return <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">{avisos}</div>;
};

export default AvisosVisaoGeral;
